import * as React from 'react'
import * as formik from 'formik'
import { styled } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import { t } from '@owl-nest/localize'

import { useStep, getStepIndex, getStepProgress, Step } from '../useStep'
import { Progress } from '../components/Progress'
import { Coaching } from '../components/Coaching'
import { Actions } from '../components/Actions'
import { Tip } from '../components/Tip'
import { Buttons } from '../components/Buttons'

import { Form, SolidContent } from './styles'

type RewardTypeProps = {
  project: model.project.ProjectForOwner
}

export function RewardType({ project }: RewardTypeProps): React.ReactElement<any> {
  const { setStepValue } = useStep(project)

  return (
    <>
      <Progress
        progress={getStepProgress(Step.REWARD_TYPE)}
        step={getStepIndex(Step.REWARD_TYPE)}
        name={t('Crowdfunding type')}
      />
      <Coaching project={project}>
        {t('Imagine that you’re presenting the project to an acquaintance. What would you say?')}
      </Coaching>
      <formik.Formik
        initialValues={{
          type: undefined,
        }}
        onSubmit={(values) => {
          setStepValue({ type: values.type })
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formikBag) => {
          return (
            <StyledForm onSubmit={formikBag.handleSubmit} multiplier={3}>
              <SolidContent>
                <formik.Field name="type">
                  {({ field }: formik.FieldProps<string>) => {
                    return (
                      <plume.RadioGroupField type="big">
                        <plume.RadioField
                          name={field.name}
                          value={'presale'}
                          checked={field.value === 'presale'}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key={'presale'}
                          label={
                            <>
                              {t('I want to presell an amount of units to fund my project')}
                              <plume.illustrations.twoToned.BookAndStuff size={50} />
                            </>
                          }
                          description={t("Example: I'm selling 100 t-shirts")}
                        />
                        <plume.RadioField
                          name={field.name}
                          value={'project'}
                          checked={field.value === 'project'}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key={'project'}
                          label={
                            <>
                              {t('I want to raise money to fund my project')}
                              <plume.illustrations.twoToned.Coins size={50} />
                            </>
                          }
                          description={t("Example: I'm raising 3 000$")}
                        />
                      </plume.RadioGroupField>
                    )
                  }}
                </formik.Field>
              </SolidContent>

              <Actions>
                <Tip>{t('Nothing final, you can edit this information later.')}</Tip>
                <Buttons>
                  <plume.LinkAsButton kind="secondary" type="button" onClick={() => setStepValue()}>
                    {t('Skip')}
                  </plume.LinkAsButton>
                  <plume.Button kind="primary">{t('Next')}</plume.Button>
                </Buttons>
              </Actions>
            </StyledForm>
          )
        }}
      </formik.Formik>
    </>
  )
}

const StyledForm = styled(Form)`
  ${plume.RadioGroupField} {
    max-width: 600px;

    ${plume.illustrations.Illustration} {
      position: absolute;
      top: 25%;
      right: 13px;
    }

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      ${plume.illustrations.Illustration} {
        position: absolute;
        top: 10px;
        right: 13px;
      }
    }
  }
`
